import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiCheck, mdiClose, mdiDelete, mdiFileSign, mdiLoading, mdiPencil } from '@mdi/js'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { Checkbox } from '../ui/checkbox'
import { typeHasPII, withTypePIIFlag } from '../../lib/pii'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { SearchableSelect } from '../SearchableSelect'
import { useLocale } from '../../contexts/LocaleContext'
import { useCan } from '../../contexts/PermissionsContext'
import { contractTypeDisplayName, documentTypeDisplayName } from './display-names'
import type { components } from '../../api/schema'
import { EmptyState } from '../ui/empty-state'
import { ContractTypeExtractionReliability } from '../contracts/ContractTypeExtractionReliability'
import { AssetTypeCodesField } from '../relation-types/AssetTypeCodesField'

type ContractType = components['schemas']['ContractType']
type DocumentType = components['schemas']['DocumentType']

export const NONE_OPTION = '__none__'

/** Uids of `uid` and every contract type below it — never valid parents (cycle guard). */
export function subtreeUids(contractTypes: ContractType[], uid: string): Set<string> {
    const result = new Set<string>([uid])
    let grew = true
    let guard = 0
    while (grew && guard++ < 100) {
        grew = false
        for (const contractType of contractTypes) {
            const parent = contractType.parent_contract_type_uid
            if (parent && result.has(parent) && !result.has(contractType.uid)) {
                result.add(contractType.uid)
                grew = true
            }
        }
    }
    return result
}

export interface ContractTypeUpdatePayload {
    code: string
    names: { [key: string]: string }
    document_type_uid: string | null
    parent_contract_type_uid: string | null
    lightweight: boolean
    /** Type-level policy tokens; only `pii` is defined today. Replaces the
     *  whole array server-side — always send the merged list. */
    flags: string[]
    /** Asset type codes on which a contract of this type is expected (the site
     *  checklist). Replaces the whole list server-side; [] clears it. */
    expected_on: string[]
}

/**
 * Inline editing commits one field at a time, so every write is a PARTIAL
 * body. The PATCH endpoint is tri-state per field (omit = keep, null = clear,
 * value = set) — see `UpdateContractTypeRequest` in api/realassets-api.yaml.
 */
export type ContractTypeUpdatePatch = Partial<ContractTypeUpdatePayload>

/** The inline-editable fields, used as per-field saving/error keys. */
type EditableField =
    | 'code'
    | 'name_en'
    | 'name_fr'
    | 'parent'
    | 'document_type'
    | 'lightweight'
    | 'pii'
    | 'expected_on'

interface ContractTypeEditorProps {
    /** Organization code, for the asset type picker of "Expected on". */
    org: string
    contractType: ContractType | null
    /** Outline number computed by the tree (`1.2.1`), so both panes agree. */
    number?: string
    allContractTypes: ContractType[]
    documentTypes: DocumentType[]
    onUpdate: (data: ContractTypeUpdatePatch) => Promise<void>
    onDelete: () => Promise<void>
    onSelectContractType: (uid: string) => void
    isDeleting?: boolean
}

interface InlineTextFieldProps {
    label: string
    value: string
    /** When false the field renders its stored value as plain, non-editable
     *  text: no click-to-edit trigger, no hover affordance, no save/cancel. */
    canWrite: boolean
    placeholder: string
    testId: string
    isSaving: boolean
    error?: string
    editLabel: string
    saveLabel: string
    cancelLabel: string
    savingLabel: string
    onCommit: (next: string) => Promise<void>
}

/**
 * Click-to-edit text field, per the canonical pattern (§3 of
 * docs/frontend/patterns.md, rendered at `/$org/patterns#pattern-inline-edit-text`):
 * pencil on hover, Enter or the check button commits, Escape cancels, blur
 * commits. Each commit is its own PATCH — there is no page-level Save.
 */
function InlineTextField({
    label,
    value,
    canWrite,
    placeholder,
    testId,
    isSaving,
    error,
    editLabel,
    saveLabel,
    cancelLabel,
    savingLabel,
    onCommit,
}: InlineTextFieldProps) {
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState(value)
    // Closing the input unmounts it, which can still fire a blur in some
    // browsers — that blur would either re-commit what Enter just wrote
    // (double PATCH) or commit the very edit Escape just cancelled. This flag
    // makes the first of commit/cancel win and every later call a no-op.
    const settledRef = useRef(false)

    // A live refetch (or picking another contract type) must not silently
    // keep a stale draft around for the next time this field is opened.
    useEffect(() => {
        if (!isEditing) setDraft(value)
    }, [value, isEditing])

    const commit = useCallback(async () => {
        if (settledRef.current) return
        settledRef.current = true
        setIsEditing(false)
        if (draft === value) return
        await onCommit(draft)
    }, [draft, value, onCommit])

    const cancel = useCallback(() => {
        settledRef.current = true
        setDraft(value)
        setIsEditing(false)
    }, [value])

    return (
        <div>
            <Label htmlFor={`${testId}-input`}>{label}</Label>
            {!canWrite ? (
                // Read-only: the value stays on screen, it just stops being a
                // button (no cursor-pointer, no hover border, no pencil).
                <div
                    className="flex min-h-9 w-full items-center gap-2 rounded border border-transparent px-2 py-1 text-left text-sm"
                    data-testid={`${testId}-value`}
                >
                    <span className={value ? 'truncate' : 'truncate text-muted-foreground'}>
                        {value || placeholder}
                    </span>
                </div>
            ) : isEditing ? (
                <div className="flex items-center gap-1">
                    <Input
                        id={`${testId}-input`}
                        autoFocus
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={() => void commit()}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                void commit()
                            }
                            if (e.key === 'Escape') {
                                e.preventDefault()
                                cancel()
                            }
                        }}
                        data-testid={testId}
                    />
                    <Button
                        variant="ghost"
                        size="icon"
                        // onMouseDown, not onClick: the input's blur fires
                        // first on a plain click and would unmount this button
                        // before its click ever lands.
                        onMouseDown={e => {
                            e.preventDefault()
                            void commit()
                        }}
                        aria-label={saveLabel}
                        title={saveLabel}
                        data-testid={`${testId}-save`}
                    >
                        <AppIcon path={mdiCheck} size="sm" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onMouseDown={e => {
                            e.preventDefault()
                            cancel()
                        }}
                        aria-label={cancelLabel}
                        title={cancelLabel}
                        data-testid={`${testId}-cancel`}
                    >
                        <AppIcon path={mdiClose} size="sm" />
                    </Button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => {
                        settledRef.current = false
                        setDraft(value)
                        setIsEditing(true)
                    }}
                    className="group flex min-h-9 w-full items-center gap-2 rounded border border-transparent px-2 py-1 text-left text-sm hover:border-input hover:bg-muted/50"
                    aria-label={editLabel}
                    title={editLabel}
                    data-testid={`${testId}-value`}
                >
                    <span className={value ? 'truncate' : 'truncate text-muted-foreground'}>
                        {value || placeholder}
                    </span>
                    {isSaving ? (
                        // Wrapped in a span: the MDI icon component does not
                        // forward unknown props (data-testid) to the <svg>.
                        <span
                            className="ml-auto shrink-0 text-muted-foreground"
                            title={savingLabel}
                            data-testid={`${testId}-saving`}
                        >
                            <AppIcon path={mdiLoading} size="xs" spin />
                        </span>
                    ) : (
                        <AppIcon tone="muted" path={mdiPencil} size="xs" className="ml-auto shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                    )}
                </button>
            )}
            {error && (
                <p className="mt-1 text-xs text-destructive" role="status" data-testid={`${testId}-error`}>
                    {error}
                </p>
            )}
        </div>
    )
}

export function ContractTypeEditor({
    org,
    contractType,
    number,
    allContractTypes,
    documentTypes,
    onUpdate,
    onDelete,
    onSelectContractType,
    isDeleting = false,
}: ContractTypeEditorProps) {
    const { t } = useTranslation('types')
    // Multilingual `names` maps are resolved against the active UI locale, and
    // the locale is a dependency of every memo below so a language switch
    // re-labels this pane live.
    const { locale } = useLocale()
    // Contract types are configuration: without `configuration.write` this
    // pane is a viewer. Every write affordance below is hidden (actions) or
    // turned read-only (fields) — the backend is what enforces.
    const canWrite = useCan('configuration.write')
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Per-field saving/error state — there is no page-level form to fail, so
    // a failed commit annotates only the field that produced it.
    const [savingField, setSavingField] = useState<EditableField | null>(null)
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<EditableField, string>>>({})

    const contractTypeUid = contractType?.uid
    useEffect(() => {
        // Selecting another contract type clears anything left over from the
        // previous one. Nothing else is reset on identity changes: a live
        // refetch must not wipe an in-flight error the user hasn't seen yet.
        setError(null)
        setFieldErrors({})
        setSavingField(null)
    }, [contractTypeUid])

    const commitField = useCallback(
        async (field: EditableField, patch: ContractTypeUpdatePatch) => {
            setSavingField(field)
            setFieldErrors(prev => {
                const next = { ...prev }
                delete next[field]
                return next
            })
            try {
                await onUpdate(patch)
            } catch (e) {
                setFieldErrors(prev => ({
                    ...prev,
                    [field]: t('contract_types.editor.update_error') + ' ' + (e instanceof Error ? e.message : ''),
                }))
            } finally {
                setSavingField(null)
            }
        },
        [onUpdate, t]
    )

    // The whole `names` map is always sent, not just the edited locale — a
    // PATCH with `{names: {en: …}}` would replace the jsonb column and drop
    // the French label.
    const commitName = useCallback(
        (loc: 'en' | 'fr', value: string) => {
            const merged = { ...(contractType?.names || {}), [loc]: value }
            return commitField(loc === 'en' ? 'name_en' : 'name_fr', { names: merged })
        },
        [contractType, commitField]
    )

    // Same array-replace hazard as `names`: `flags` overwrites the column, so
    // the `pii` token is toggled inside the type's CURRENT flags rather than
    // written on its own. Flipping it re-projects `pii:presumed` onto every
    // contract of the type (human verdicts are left alone).
    const commitPII = useCallback(
        (enabled: boolean) => commitField('pii', { flags: withTypePIIFlag(contractType?.flags, enabled) }),
        [contractType, commitField]
    )

    const commitCode = useCallback(
        (value: string) => commitField('code', { code: value }),
        [commitField]
    )

    const parentOptions = useMemo(() => {
        if (!contractType) return []
        const excluded = subtreeUids(allContractTypes, contractType.uid)
        return [
            { value: NONE_OPTION, label: t('contract_types.no_parent') },
            ...allContractTypes
                .filter(ct => !excluded.has(ct.uid))
                .map(ct => ({ value: ct.uid, label: contractTypeDisplayName(ct, locale) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ]
    }, [allContractTypes, contractType, locale, t])

    const documentTypeOptions = useMemo(
        () => [
            { value: NONE_OPTION, label: t('contract_types.no_document_type') },
            ...documentTypes
                .map(dt => ({ value: dt.uid, label: documentTypeDisplayName(dt, locale) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ],
        [documentTypes, locale, t]
    )

    const children = useMemo(
        () =>
            allContractTypes
                .filter(ct => ct.parent_contract_type_uid === contractType?.uid)
                .sort((a, b) => contractTypeDisplayName(a, locale).localeCompare(contractTypeDisplayName(b, locale))),
        [allContractTypes, contractType, locale]
    )

    if (!contractType) {
        return (
            <div
                className="flex h-full items-center justify-center p-8"
                data-testid="contract-type-editor-empty"
            >
                <EmptyState
                    variant="inline"
                    icon={mdiFileSign}
                    title={t('contract_types.editor.none_selected_title')}
                    description={t('contract_types.editor.none_selected_description')}
                />
            </div>
        )
    }

    const documentType = contractType.document_type_uid
        ? documentTypes.find(dt => dt.uid === contractType.document_type_uid)
        : undefined

    const handleDelete = async () => {
        setError(null)
        setConfirmDeleteOpen(false)
        try {
            await onDelete()
        } catch (e) {
            setError(t('contract_types.editor.delete_error') + ' ' + (e instanceof Error ? e.message : ''))
        }
    }

    return (
        <div className="flex h-full flex-col" data-testid="contract-type-editor">
            <div className="border-b p-4 md:p-6">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="flex items-baseline gap-2 text-xl font-bold md:text-2xl">
                            {number && (
                                <span
                                    className="shrink-0 font-mono text-base font-normal tabular-nums text-muted-foreground"
                                    title={t('contract_types.editor.outline_number_hint')}
                                    data-testid="contract-type-editor-number"
                                >
                                    {number}
                                </span>
                            )}
                            <span className="truncate" data-testid="contract-type-editor-title">
                                {contractTypeDisplayName(contractType, locale)}
                            </span>
                        </h2>
                        {documentType && (
                            <Badge variant="secondary" className="mt-1 text-xs" data-testid="contract-type-editor-type-badge">
                                {documentTypeDisplayName(documentType, locale)}
                            </Badge>
                        )}
                    </div>
                    {/* No Edit button: every field below is click-to-edit and
                        commits its own PATCH, so Delete is the only header
                        action left (icon-only, per front/CLAUDE.md). */}
                    <div className="flex shrink-0 items-center gap-2">
                        {canWrite && (
                            <Button
                                variant="destructive"
                                size="icon"
                                onClick={() => setConfirmDeleteOpen(true)}
                                disabled={isDeleting}
                                aria-label={t('contract_types.editor.delete')}
                                title={t('contract_types.editor.delete')}
                                data-testid="contract-type-editor-delete"
                            >
                                <AppIcon path={mdiDelete} size="sm" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto p-4 md:p-6">
                {error && (
                    <div
                        className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                        role="status"
                        data-testid="contract-type-editor-error"
                    >
                        {error}
                    </div>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">{t('contract_types.editor.basic_info')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <InlineTextField
                            label={t('contract_types.editor.code_label')}
                            value={contractType.code || ''}
                            canWrite={canWrite}
                            placeholder={t('contract_types.editor.not_set')}
                            testId="contract-type-editor-code"
                            isSaving={savingField === 'code'}
                            error={fieldErrors.code}
                            editLabel={t('contract_types.editor.edit_field', {
                                field: t('contract_types.editor.code_label'),
                            })}
                            saveLabel={t('contract_types.editor.save')}
                            cancelLabel={t('contract_types.editor.cancel')}
                            savingLabel={t('contract_types.editor.saving')}
                            onCommit={commitCode}
                        />

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <InlineTextField
                                label={`🇺🇸 ${t('contract_types.editor.name_en_label')}`}
                                value={contractType.names?.en || ''}
                                canWrite={canWrite}
                                placeholder={t('contract_types.editor.not_set')}
                                testId="contract-type-editor-name-en"
                                isSaving={savingField === 'name_en'}
                                error={fieldErrors.name_en}
                                editLabel={t('contract_types.editor.edit_field', {
                                    field: t('contract_types.editor.name_en_label'),
                                })}
                                saveLabel={t('contract_types.editor.save')}
                                cancelLabel={t('contract_types.editor.cancel')}
                                savingLabel={t('contract_types.editor.saving')}
                                onCommit={value => commitName('en', value)}
                            />
                            <InlineTextField
                                label={`🇫🇷 ${t('contract_types.editor.name_fr_label')}`}
                                value={contractType.names?.fr || ''}
                                canWrite={canWrite}
                                placeholder={t('contract_types.editor.not_set')}
                                testId="contract-type-editor-name-fr"
                                isSaving={savingField === 'name_fr'}
                                error={fieldErrors.name_fr}
                                editLabel={t('contract_types.editor.edit_field', {
                                    field: t('contract_types.editor.name_fr_label'),
                                })}
                                saveLabel={t('contract_types.editor.save')}
                                cancelLabel={t('contract_types.editor.cancel')}
                                savingLabel={t('contract_types.editor.saving')}
                                onCommit={value => commitName('fr', value)}
                            />
                        </div>

                        {/* Selects need no edit mode: picking an option IS the
                            commit, so each one PATCHes just its own field. */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label>{t('contract_types.editor.parent_label')}</Label>
                                    {savingField === 'parent' && (
                                        <span
                                            className="text-muted-foreground"
                                            title={t('contract_types.editor.saving')}
                                            data-testid="contract-type-editor-parent-saving"
                                        >
                                            <AppIcon path={mdiLoading} size="xs" spin />
                                        </span>
                                    )}
                                </div>
                                <SearchableSelect
                                    options={parentOptions}
                                    value={contractType.parent_contract_type_uid || NONE_OPTION}
                                    onValueChange={value => {
                                        const next = value === NONE_OPTION || value === '' ? null : value
                                        if (next === (contractType.parent_contract_type_uid || null)) return
                                        void commitField('parent', { parent_contract_type_uid: next })
                                    }}
                                    disabled={!canWrite || savingField === 'parent'}
                                    placeholder={t('contract_types.select_parent_placeholder')}
                                    searchPlaceholder={t('contract_types.editor.search_placeholder')}
                                    emptyMessage={t('contract_types.editor.no_results')}
                                    data-testid="contract-type-editor-parent"
                                />
                                {fieldErrors.parent && (
                                    <p
                                        className="mt-1 text-xs text-destructive"
                                        role="status"
                                        data-testid="contract-type-editor-parent-error"
                                    >
                                        {fieldErrors.parent}
                                    </p>
                                )}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label>{t('contract_types.editor.document_type_label')}</Label>
                                    {savingField === 'document_type' && (
                                        <span
                                            className="text-muted-foreground"
                                            title={t('contract_types.editor.saving')}
                                            data-testid="contract-type-editor-document-type-saving"
                                        >
                                            <AppIcon path={mdiLoading} size="xs" spin />
                                        </span>
                                    )}
                                </div>
                                <SearchableSelect
                                    options={documentTypeOptions}
                                    value={contractType.document_type_uid || NONE_OPTION}
                                    onValueChange={value => {
                                        const next = value === NONE_OPTION || value === '' ? null : value
                                        if (next === (contractType.document_type_uid || null)) return
                                        void commitField('document_type', { document_type_uid: next })
                                    }}
                                    disabled={!canWrite || savingField === 'document_type'}
                                    placeholder={t('contract_types.select_document_type_placeholder')}
                                    searchPlaceholder={t('contract_types.editor.search_placeholder')}
                                    emptyMessage={t('contract_types.editor.no_results')}
                                    data-testid="contract-type-editor-document-type"
                                />
                                {fieldErrors.document_type && (
                                    <p
                                        className="mt-1 text-xs text-destructive"
                                        role="status"
                                        data-testid="contract-type-editor-document-type-error"
                                    >
                                        {fieldErrors.document_type}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="contract-type-editor-lightweight"
                                checked={!!contractType.lightweight}
                                disabled={!canWrite || savingField === 'lightweight'}
                                onCheckedChange={checked =>
                                    void commitField('lightweight', { lightweight: checked === true })
                                }
                                data-testid="contract-type-editor-lightweight"
                            />
                            <Label
                                htmlFor="contract-type-editor-lightweight"
                                className={canWrite ? 'cursor-pointer' : undefined}
                            >
                                {t('contract_types.editor.lightweight_label')}
                            </Label>
                            {savingField === 'lightweight' && (
                                <AppIcon tone="muted" path={mdiLoading} size="xs" spin />
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('contract_types.editor.lightweight_hint')}
                        </p>
                        {fieldErrors.lightweight && (
                            <p
                                className="text-xs text-destructive"
                                role="status"
                                data-testid="contract-type-editor-lightweight-error"
                            >
                                {fieldErrors.lightweight}
                            </p>
                        )}

                        {/* PII policy (spec 2026-08-13-02 §8). */}
                        <div className="flex items-center gap-2 pt-2">
                            <Checkbox
                                id="contract-type-editor-pii"
                                checked={typeHasPII(contractType.flags)}
                                disabled={!canWrite || savingField === 'pii'}
                                onCheckedChange={checked => void commitPII(checked === true)}
                                data-testid="contract-type-editor-pii"
                            />
                            <Label
                                htmlFor="contract-type-editor-pii"
                                className={canWrite ? 'cursor-pointer' : undefined}
                            >
                                {t('pii.type_toggle', { ns: 'common' })}
                            </Label>
                            {savingField === 'pii' && (
                                <AppIcon tone="muted" path={mdiLoading} size="xs" spin />
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('pii.type_toggle_hint', { ns: 'common' })}
                        </p>
                        {fieldErrors.pii && (
                            <p
                                className="text-xs text-destructive"
                                role="status"
                                data-testid="contract-type-editor-pii-error"
                            >
                                {fieldErrors.pii}
                            </p>
                        )}

                        {/* Expected on (spec 2026-10-11-16): each pick or removal
                            is a commit, like the selects above. */}
                        <div className="relative pt-2">
                            {savingField === 'expected_on' && (
                                <span
                                    className="absolute right-0 top-2 text-muted-foreground"
                                    title={t('contract_types.editor.saving')}
                                    data-testid="contract-type-editor-expected-on-saving"
                                >
                                    <AppIcon path={mdiLoading} size="xs" spin />
                                </span>
                            )}
                            <AssetTypeCodesField
                                org={org}
                                label={t('contract_types.editor.expected_on_label')}
                                value={contractType.expected_on ?? []}
                                onChange={codes => void commitField('expected_on', { expected_on: codes })}
                                disabled={!canWrite || savingField === 'expected_on'}
                                testIdPrefix="contract-type-editor-expected-on"
                                emptyLabel={t('contract_types.editor.expected_on_empty')}
                                hint={t('contract_types.editor.expected_on_hint')}
                            />
                            {fieldErrors.expected_on && (
                                <p
                                    className="mt-1 text-xs text-destructive"
                                    role="status"
                                    data-testid="contract-type-editor-expected-on-error"
                                >
                                    {fieldErrors.expected_on}
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">
                            {t('contract_types.editor.children_title', { count: children.length })}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {children.length === 0 ? (
                            <EmptyState
                                variant="inline"
                                title={t('contract_types.editor.no_children')}
                                data-testid="contract-type-editor-no-children"
                            />
                        ) : (
                            <div className="space-y-1">
                                {children.map(child => (
                                    <button
                                        key={child.uid}
                                        type="button"
                                        onClick={() => onSelectContractType(child.uid)}
                                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted/50"
                                        data-testid={`contract-type-editor-child-${child.uid}`}
                                    >
                                        <AppIcon tone="muted" path={mdiFileSign} size="sm" className="shrink-0" />
                                        <span className="truncate">{contractTypeDisplayName(child, locale)}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <ContractTypeExtractionReliability org={org} contractTypeUid={contractType.uid} contractTypeCode={contractType.code} />
            </div>

            <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
                <AlertDialogContent data-testid="contract-type-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('contract_types.editor.delete_dialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('contract_types.editor.delete_dialog.description', {
                                name: contractTypeDisplayName(contractType, locale),
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="contract-type-delete-cancel">
                            {t('contract_types.editor.delete_dialog.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} data-testid="contract-type-delete-confirm">
                            {t('contract_types.editor.delete_dialog.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
